<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { storeToRefs } from 'pinia';
import Dropdown from '@/components/Globals/Inputs/Dropdown.vue';
import OutlineButton from '../Globals/Buttons/OutlineButton.vue';
import AlarmsInfo from './AlarmsInfo.vue'
import { useI18n } from '@/composable/useI18n';
import { useAlarmProfileStore } from '@/store/alarmProfile';
import { useSensorStore } from '@/store/sensor';
import { useErrorStore } from '@/store/error';
import { resolveLinkedProfileId, shouldBlockAlarmActivation } from '@/utils/sensor-alarm-profile';
import AlarmNoDataForm from './Forms/AlarmNoDataForm.vue';
import AlarmInterruptionForm from './Forms/AlarmInterruptionForm.vue';
import AlarmLowLoadForm from './Forms/AlarmLowLoadForm.vue';
import PeakHourAlarmForm from './Forms/PeakHourAlarmForm.vue';
import AlarmDefrostTimeForm from './Forms/AlarmDefrostTimeForm.vue';
import AlarmBurntResistorForm from './Forms/AlarmBurntResistorForm.vue';
import AlarmEmptyMixerForm from './Forms/AlarmEmptyMixerForm.vue';
import AlarmOutsideRegularTimeForm from './Forms/AlarmOutsideRegularTimeForm.vue';
import AlarmPowerFactorForm from './Forms/AlarmPowerFactorForm.vue';
import AlarmTemperatureForm from './Forms/AlarmTemperatureForm.vue';
import AlarmDoorOpenForm from './Forms/AlarmDoorOpenForm.vue';
import AlarmOvervoltageForm from './Forms/AlarmOvervoltageForm.vue';

const { t } = useI18n()
const sensorStore = useSensorStore();
const profileStore = useAlarmProfileStore();
const { selectedSensor, selectedSensorDetails } = storeToRefs(sensorStore);
const currentSensor = computed(() => selectedSensor.value || selectedSensorDetails.value);
const sensorType = computed(() => currentSensor.value?.sensor_type || null);

const {
    listInterruptionProfiles,
    listLowLoadProfiles,
    listPeakHourProfiles,
    listDefrostTimeProfiles,
    listEmptyMixerProfiles,
    listOutsideRegularTimeProfiles,
    listPowerFactorProfiles,
    listNoDataProfiles,
    listCriticalTemperatureProfiles,
    listOvervoltageProfiles,
    listDoorOpenProfiles
} = storeToRefs(profileStore);

const isOpen = ref(false); 
const openForm = ref(false);
const profileSelected = ref(null);
const hasFetched = ref(false);
const formType = ref<'create' | 'edit'>('create');

const handleProfileSelected = (option: any) => {
    profileSelected.value = option;
}

const props = defineProps < {
    label: string
    isActive: boolean
    profileId?: number | null
}>()

const emit = defineEmits (['toggle'])

const strategyMap = {
    'Interrupção': {
        fetch: profileStore.fetchInterruptionAlarmProfiles,
        list: listInterruptionProfiles,
        update: sensorStore.updateInterruptionSettings,
    },
    'Carga baixa': {
        fetch: profileStore.fetchLowLoadAlarmProfiles,
        list: listLowLoadProfiles,
        update: sensorStore.updateLowLoadSettings,
    },

    'Ativação em horário de pico': {
        fetch: profileStore.fetchPeakHourAlarmProfiles,
        list: listPeakHourProfiles,
        update: sensorStore.updatePeakHourSettings,
    },

    'Tempo de degelo': {
        fetch: profileStore.fetchDefrostTimeAlarmProfiles,
        list: listDefrostTimeProfiles,
        update: sensorStore.updateDefrostTimeSettings,
    },

    'Resistência queimada': {
        fetch: profileStore.fetchDefrostTimeAlarmProfiles,
        list: listDefrostTimeProfiles,
        update: sensorStore.updateDefrostTimeSettings,
    },

    'Misturador vazio': {
        fetch: profileStore.fetchEmptyMixerAlarmProfiles,
        list: listEmptyMixerProfiles,
        update: sensorStore.updateEmptyMixerSettings,
    },

    'Funcionamento fora do horário padrão': {
        fetch: profileStore.fetchOutsideRegularTimeAlarmProfiles,
        list: listOutsideRegularTimeProfiles,
        update: sensorStore.updateIOutsideRegularTimeSettings,
    },

    'Fator de potência': {
        fetch: profileStore.fetchPowerFactorAlarmProfiles,
        list: listPowerFactorProfiles,
        update: sensorStore.updatePowerFactorSettings,
    },

    'Ausência de dados': {
        fetch: profileStore.fetchNoDataAlarmProfiles,
        list: listNoDataProfiles,
        update: sensorStore.updateNoDataSettings,
    },

    'Abertura de porta': {
        fetch: profileStore.fetchDoorOpenAlarmProfiles,
        list: listDoorOpenProfiles,
        update: sensorStore.updateDoorOpenSettings,
    },

    'Temperatura crítica': {
        fetch: profileStore.fetchCriticalTemperatureAlarmProfiles,
        list: listCriticalTemperatureProfiles,
        update: sensorStore.updateTemperatureSettings,
    },

    'Sobretensão': {
        fetch: profileStore.fetchOvervoltageAlarmProfiles,
        list: listOvervoltageProfiles,
        update: sensorStore.updateOvervoltageSettings,
    },

}

const options = computed(() => {
    const list = strategyMap[props.label]?.list?.value || [];
    return list;
})

const linkedProfileId = computed(() =>
    resolveLinkedProfileId(profileSelected.value?.id, props.profileId),
)

const isActivationBlocked = computed(() => !props.isActive && linkedProfileId.value == null)

function handleMonitoringToggle(nextActive: boolean) {
    if (shouldBlockAlarmActivation(props.isActive, nextActive, linkedProfileId.value)) {
        useErrorStore().setErrors('erro:', [t('sensor.alarms_config.profile_required_activate')])
        return
    }

    emit('toggle', linkedProfileId.value ?? undefined)
}

const handleSaveSettings = async () => {
    if (!profileSelected.value) {
        useErrorStore().setErrors('erro:', [t('sensor.alarms_config.profile_required_save')])
        return; 
    }
    
    if (!currentSensor.value || !currentSensor.value.id) {
        useErrorStore().setErrors('erro:', [t('sensor.alarms_config.sensor_unavailable')])
        return
    }
    
    const currentStrategy = strategyMap[props.label];
    if(currentStrategy && currentStrategy.update) {
        if (currentStrategy === strategyMap['Ausência de dados']) {
            await currentStrategy.update(currentSensor.value.id, sensorType.value, profileSelected.value?.id, props.isActive);
        } else{
            await currentStrategy.update(currentSensor.value.id, profileSelected.value?.id);
        }
    }
}

const toggleAccordion = async () => {
    isOpen.value = !isOpen.value;
    openForm.value = false;

    if(isOpen.value && !hasFetched.value) {
        const currentStrategy = strategyMap[props.label];
            if(currentStrategy && currentStrategy.fetch) {
                await currentStrategy.fetch({});
            hasFetched.value = true;

            if(props.profileId) {
                const preSelectedProfile = options.value.find((option: any) => option.id === props.profileId);
                profileSelected.value = preSelectedProfile || null;
            }
            
        }
    }
}

const toggleForm = () => {
    openForm.value = !openForm.value;
    formType.value = 'create';
}
const toggleEditForm = () => {
    formType.value = 'edit';
    openForm.value = true;
}

const restrictEditPublicProfile = computed(() => {
    if (profileSelected.value && profileSelected.value.is_public_profile) {
        return true;
    }
    return false;
});

watch(options, (newOptions) => {
    if (profileSelected.value && newOptions.length > 0) {
        const updatedProfile = newOptions.find((option: any) => option.id === profileSelected.value.id);
        if (updatedProfile) {
            profileSelected.value = updatedProfile;
        }
    }
}, { deep: true});

</script>
<template>
    <div class="border border-stroke bg-white transition-all duration-300 p-0.5 mb-6">
        <div class="p-3 flex items-center justify-between cursor-pointer hover:bg-gray-2 transition" @click="toggleAccordion">
            <div class="flex items-center gap-2">
                <span v-if="isOpen" class="px-0 py-0 pi pi-chevron-down" style="font-size: 0.75rem"></span>
                <span v-if="!isOpen" class="px-0 py-0 pi pi-chevron-right" style="font-size: 0.75rem"></span>
                <span class="text-m font-medium text-black">{{ label }}</span>
            </div>
            <span v-if="isActive" :class="`px-2 py-1 text-sm font-medium text-white bg-blue-600`">{{ t('sensor.alarms_config.status_active') }}</span> 
            <span v-if="!isActive" :class="`px-2 py-1 text-sm font-medium text-gray-500 bg-gray-200`">{{ t('sensor.alarms_config.status_inactive') }}</span>
        </div>
        <transition 
            enter-active-class="transition-all duration-300 ease-out"
            leave-active-class="transition-all duration-230 ease-in"
            enter-from-class="transform -translate-y-4 opacity-0"
            leave-to-class="transform -translate-y-1 opacity-0"
        >
            <div v-if="isOpen && !openForm" class="border-t border-stroke bg-white">
                <div class="p-5 space-y-5">
                    <div>
                        <label
                            class="block text-g text-body mb-1.5">{{ t('sensor.alarms_config.profile_label') }}</label>

                        <div class="flex items-center gap-2">
                            <div class="w-full">
                                <Dropdown 
                                name="alarms_profile"
                                :options="options"
                                :label-attribute="'name'"
                                :has-all-option="false"
                                @option-selected="handleProfileSelected"
                                :already-selected-option="profileSelected"
                                />
                            </div>
                            <div class="flex-shrink-0">
                                <OutlineButton 
                                    :label="t('sensor.alarms_config.add_new')"
                                    class="!h-auto !py-3.5 !px-4 !text-sm !border-stroke !bg-white !shadow-sm hover:!bg-gray-2 !flex !items-center !justify-center"
                                    @click="toggleForm"
                                />
                            </div>
                        </div>
                    </div>
                    <div class="space-y-3 bg-gray-2 p-3 border border-stroke">
                        <AlarmsInfo 
                        :label="props.label"
                        :settings="profileSelected?.settings"
                        />
                    </div>
                    <div class="flex w-full gap-2">
                        <OutlineButton 
                            :label="t('sensor.alarms_config.save')"
                            class="!h-auto flex-1 !py-2 !text-sm !px-2 !text-white !border-blue-600 !bg-blue-600 !shadow-sm hover:!bg-blue-700 !flex !items-center !justify-center whitespace-nowrap"
                            @click="handleSaveSettings"
                        />
                        <div class="relative group w-full flex-1">
                            <div v-if="restrictEditPublicProfile"
                                class="pointer-events-none absolute bottom-full left-1/2 z-10 mb-2 w-max -translate-x-1/2 bg-black px-3 py-2 text-xs text-white opacity-0 transition-opacity group-hover:opacity-100 whitespace-nowrap shadow-lg">
                                {{ t('sensor.alarms_config.cannot_edit_public_profile') }}
                                <div class="absolute left-1/2 top-full h-0 w-0 -translate-x-1/2 border-x-4 border-t-4 border-x-transparent border-t-black"></div>
                            </div>
                            <OutlineButton
                                :label="t('sensor.alarms_config.edit_profile')"
                                :disabled="restrictEditPublicProfile"
                                :class="['!h-auto w-full flex-1 !py-2 !text-sm !px-2 !border-stroke !bg-white !shadow-sm hover:!bg-gray-2 !flex !items-center !justify-center whitespace-nowrap',
                                    restrictEditPublicProfile ? 'opacity-50' : 'hover:!bg-gray-2'
                                ]"
                                @click="toggleEditForm"
                            />
                        </div>
                    </div>
                    <div class="border-t border-stroke pt-1"></div>
                    <div class="flex items-center justify-between bg-gray-2 p-3 border border-stroke">
                        <div class="flex flex-col">
                            <span
                                class="text-xs font-bold text-black uppercase tracking-wide">{{ t('sensor.alarms_config.monitoring_label') }}</span>
                            <span id="status-text"
                                class="text-[10px] font-bold mt-0.5"
                                :class="props.isActive ? 'text-blue-600' : 'text-gray-500'">
                                {{ props.isActive ? t('sensor.alarms_config.monitoring_active') : t('sensor.alarms_config.monitoring_inactive') }}
                            </span>
                        </div>
                        <div class="relative group">
                            <div
                                v-if="isActivationBlocked"
                                class="pointer-events-none absolute bottom-full right-0 z-10 mb-2 w-max max-w-xs bg-black px-3 py-2 text-xs text-white opacity-0 transition-opacity group-hover:opacity-100 shadow-lg">
                                {{ t('sensor.alarms_config.activation_blocked_hint') }}
                                <div class="absolute right-4 top-full h-0 w-0 border-x-4 border-t-4 border-x-transparent border-t-black"></div>
                            </div>
                            <ElSwitch
                                style="--el-switch-on-color: #2563eb"
                                :model-value="props.isActive"
                                :disabled="isActivationBlocked"
                                @change="handleMonitoringToggle" />
                        </div>
                    </div>

                </div>
            </div>
        </transition>
        <transition 
            enter-active-class="transition-all duration-300 ease-out"
            leave-active-class="transition-all duration-230 ease-in"
            enter-from-class="transform -translate-y-4 opacity-0"
            leave-to-class="transform -translate-y-1 opacity-0"
        >
            <div v-if="isOpen && openForm" class="border-t border-stroke bg-white">
                <div class="p-5">
                    <AlarmNoDataForm v-if="props.label === 'Ausência de dados'" :form-type="formType" :current-profile="profileSelected" @cancel="openForm=false" />
                    <AlarmInterruptionForm v-if="props.label === 'Interrupção'" :form-type="formType" :current-profile="profileSelected" @cancel="openForm=false" />
                    <AlarmLowLoadForm v-if="props.label === 'Carga baixa'" :form-type="formType" :current-profile="profileSelected" @cancel="openForm=false" />
                    <PeakHourAlarmForm v-if="props.label === 'Ativação em horário de pico'" :form-type="formType" :current-profile="profileSelected" @cancel="openForm=false" />
                    <AlarmDefrostTimeForm v-if="props.label === 'Tempo de degelo'" :form-type="formType" :current-profile="profileSelected" @cancel="openForm=false" />
                    <AlarmBurntResistorForm v-if="props.label === 'Resistência queimada'" :form-type="formType" :current-profile="profileSelected" @cancel="openForm=false" />
                    <AlarmEmptyMixerForm v-if="props.label === 'Misturador vazio'" :form-type="formType" :current-profile="profileSelected" @cancel="openForm=false" />
                    <AlarmOutsideRegularTimeForm v-if="props.label === 'Funcionamento fora do horário padrão'" :form-type="formType" :current-profile="profileSelected" @cancel="openForm=false" />
                    <AlarmPowerFactorForm v-if="props.label === 'Fator de potência'" :form-type="formType" :current-profile="profileSelected" @cancel="openForm=false" />
                    <AlarmTemperatureForm v-if="props.label === 'Temperatura crítica'" :form-type="formType" :current-profile="profileSelected" @cancel="openForm=false" />
                    <AlarmDoorOpenForm v-if="props.label === 'Abertura de porta'" :form-type="formType" :current-profile="profileSelected" @cancel="openForm=false" />
                    <AlarmOvervoltageForm v-if="props.label === 'Sobretensão'" :form-type="formType" :current-profile="profileSelected" @cancel="openForm=false" />
                </div>
            </div>
        </transition>    
    </div>
</template>
